import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useInView } from "framer-motion";

import { useArticles } from "../../hooks/useArticleQueries";
import { ZP } from "../../constants/palette";
import ArticleMeta from "./ArticleMeta";
import StepMotif from "./StepMotif";
import { ARROW_NUDGE, BOX, HAIRLINE, KICKER, LINK } from "./shell";

const COUNT = 3;

/**
 * Three articles, and the way to the rest — the last thing on every marketing
 * page before its footer.
 *
 * Which three: the journal's featured pieces first, then the newest, which is
 * the order the index itself leads with. `excludeSlug` drops the article a
 * reader is already on, so an article page never recommends itself.
 *
 * Laid out as a lead and two (design file, "Articles Shelf — Options", C): the
 * first piece takes two thirds of the row with a looping image and its
 * excerpt; the other two stack beside it as a name and a title. One piece
 * carries the shelf rather than three equal blocks of text, and the row hangs
 * off a single ink rule, as the Process steps row does, instead of floating
 * as cards on a white ground.
 *
 * The article page reuses the shelf as its related reading (RelatedArticles):
 * `select` picks which pieces from the published list, `heading` and `id`
 * name the section, and `minimum` is the fewest worth showing. Each defaults
 * to the journal shelf's own behaviour.
 *
 * Renders nothing until there is something to show — while loading, on error,
 * and while nothing is published. Every article is a Sanity draft until a human
 * publishes it, and a shelf of placeholders (or an apology on a page that is
 * about something else) would be worse than no shelf. The index page is where
 * that state gets explained.
 */

/**
 * The lead's image: one of the Process page's six dot fields, chosen by the
 * article's category so the picture says something about the piece — a
 * methodology article gets Estimate's labelled gaps, a regulation article
 * Refresh's kept history. Anything unmapped gets Line up, the neutral one.
 */
const MOTIF_BY_CATEGORY = {
  Contaminants: "score",
  Methodology: "estimate",
  Analysis: "locate",
  Guides: "collect",
  Regulation: "refresh",
};
export const motifFor = (category) => MOTIF_BY_CATEGORY[category] ?? "lineup";

/**
 * The two grounds the shelf can sit on. `light` is the default, a white box on
 * the grey page. `dark` is the same layout reversed out on zp-night, for a page
 * that wants its last section to read as a dark band.
 *
 * Dark keeps white hovers rather than link blue: #2563EB is 3.72:1 on
 * zp-night and fails AA, the same reason every other dark section on the site
 * does this (constants/linkStyle.js). There the hover is carried by READ
 * brightening and the arrow's nudge.
 */
const EASE_COLOR = "transition-colors duration-150 ease-in";
const TONES = {
  light: {
    section: "bg-white",
    kicker: "text-zp-slate",
    readAll: `text-zp-ink ${LINK}`,
    rule: "border-zp-ink",
    hairline: HAIRLINE,
    tile: "bg-zp-surface",
    ink: ZP.ink,
    title: "text-zp-ink",
    titleLink: `${LINK} group-hover:text-zp-link`,
    body: "text-zp-body",
    meta: "light",
    read: `text-zp-ink ${EASE_COLOR} group-hover:text-zp-link`,
  },
  dark: {
    section: "bg-zp-night",
    kicker: "text-white/50",
    readAll: `text-white/75 ${EASE_COLOR} hover:text-white`,
    rule: "border-white/70",
    hairline: "border-white/15",
    tile: "bg-white/[0.06]",
    ink: ZP.bg,
    title: "text-white",
    titleLink: "",
    body: "text-white/70",
    meta: "dark",
    read: `text-white/60 ${EASE_COLOR} group-hover:text-white`,
  },
};

/**
 * The whole item is the link: the title's `after:` overlay stretches over it,
 * so a screen reader still hears one link named by the title. READ → is
 * decoration for the eye — it says the item goes somewhere — not a second
 * link to the same place.
 */
const TitleLink = ({ article, t }) => (
  <Link
    to={`/articles/${article.slug}`}
    className={`${t.titleLink} after:absolute after:inset-0`}
  >
    {article.title}
  </Link>
);

const Read = ({ t }) => (
  <span
    className={`inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.14em] ${t.read}`}
    aria-hidden="true"
  >
    Read
    <ArrowRight className={`h-3 w-3 ${ARROW_NUDGE}`} />
  </span>
);

const Lead = ({ article, alone, t }) => {
  const tileRef = useRef(null);
  // The motif loops only while it is on screen, like every other one.
  const inView = useInView(tileRef, { amount: 0.3 });

  return (
    <article
      className={`group relative flex flex-col gap-7 py-8 md:flex-row md:gap-9 ${
        alone ? "lg:col-span-3" : `lg:col-span-2 lg:border-r lg:pr-10 ${t.hairline}`
      }`}
    >
      <div
        ref={tileRef}
        className={`flex h-[260px] shrink-0 items-center justify-center rounded-md md:h-auto md:min-h-[340px] md:w-[340px] ${t.tile}`}
      >
        <StepMotif
          kind={motifFor(article.category)}
          play={inView}
          ink={t.ink}
          className="h-[200px] w-[200px] md:h-[264px] md:w-[264px]"
        />
      </div>
      <div className="flex flex-1 flex-col justify-between gap-6 md:pt-1.5">
        <div>
          <ArticleMeta article={article} categoryFirst tone={t.meta} />
          <h3
            className={`mt-4 font-display text-[2rem] font-light leading-[1.05] -tracking-[0.03em] sm:text-[2.4rem] ${t.title}`}
          >
            <TitleLink article={article} t={t} />
          </h3>
          {article.excerpt && (
            <p className={`mt-4 text-[15px] leading-relaxed ${t.body}`}>
              {article.excerpt}
            </p>
          )}
        </div>
        <Read t={t} />
      </div>
    </article>
  );
};

const Side = ({ article, index, t }) => (
  <article
    className={`group relative flex flex-1 flex-col justify-between gap-6 border-t py-7 lg:pl-8 ${t.hairline} ${
      index === 0 ? "lg:border-t-0" : ""
    }`}
  >
    <div>
      <ArticleMeta article={article} categoryFirst tone={t.meta} />
      <h3
        className={`mt-3.5 font-display text-[1.4rem] font-light leading-[1.15] -tracking-[0.02em] sm:text-[1.5rem] ${t.title}`}
      >
        <TitleLink article={article} t={t} />
      </h3>
    </div>
    <Read t={t} />
  </article>
);

/** Featured first, then newest — the order the index itself leads with. */
const featuredThenNewest = (candidates) =>
  [
    ...candidates.filter((a) => a.featured),
    ...candidates.filter((a) => !a.featured),
  ].slice(0, COUNT);

const ArticleShelf = ({
  excludeSlug = null,
  tone = "light",
  select = featuredThenNewest,
  heading = "From the journal",
  minimum = 1,
  id = "article-shelf-heading",
}) => {
  const { data: articles } = useArticles();
  const t = TONES[tone] ?? TONES.light;

  const candidates = (articles ?? []).filter((a) => a.slug && a.slug !== excludeSlug);
  const shown = select(candidates).slice(0, COUNT);

  if (shown.length === 0 || shown.length < minimum) return null;
  const [lead, ...rest] = shown;

  return (
    <section
      aria-labelledby={id}
      className={`${BOX} sm:py-14 ${t.section}`}
      data-tone={tone}
    >
      <div className="flex items-end justify-between gap-6 pb-[18px]">
        {/* The section's heading, in the kicker's register: the display line
            that used to sit here was taken out, but the section still needs
            a name for `aria-labelledby` to point at. */}
        <h2 id={id} className={`${KICKER} ${t.kicker}`}>
          {heading}
        </h2>
        {/* A text link, not an ink button: the button out-weighed the
            articles it introduced. */}
        <Link
          to="/articles"
          className={`group inline-flex shrink-0 items-center gap-2 text-[14px] font-medium ${t.readAll}`}
        >
          Read all articles
          <ArrowRight className={`h-3.5 w-3.5 ${ARROW_NUDGE}`} aria-hidden="true" />
        </Link>
      </div>

      <div className={`grid border-t lg:grid-cols-3 ${t.rule}`}>
        <Lead article={lead} alone={rest.length === 0} t={t} />
        {rest.length > 0 && (
          <div className="flex flex-col">
            {rest.map((article, index) => (
              <Side
                key={article._id ?? article.slug}
                article={article}
                index={index}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ArticleShelf;
